import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCelebrationStore } from '@/stores/celebrationStore';
import { useAuthStore } from '@/stores/authStore';
import type { CelebrationData } from '@/types';

interface WebSocketMessage {
  type: 'celebration' | 'sync_complete' | 'new_transaction' | 'new-sale' | 'sync-completed' | 'data-refreshed' | 'champion';
  data: any;
}

export function useWebSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const queryClient = useQueryClient();
  const { trigger, triggerChampion } = useCelebrationStore();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const connect = useCallback(() => {
    // Read token at connect time so token refreshes don't re-trigger this callback
    const accessToken = useAuthStore.getState().accessToken;
    if (!isAuthenticated || !accessToken || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    const wsUrl = import.meta.env.VITE_WS_URL ||
      `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;

    const ws = new WebSocket(`${wsUrl}?token=${accessToken}`);

    ws.onopen = () => {
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);

        switch (message.type) {
          case 'celebration':
            // Trigger celebration overlay
            const celebrationData = message.data as CelebrationData;
            trigger(celebrationData);
            break;

          case 'champion':
            // One-time 1M-ILS milestone: "We Are The Champions" show on every screen
            triggerChampion();
            break;

          case 'new-sale':
            // Backend sends new-sale with celebration data nested
            if (message.data?.celebration) {
              const saleData = message.data.celebration as CelebrationData;
              trigger(saleData);
            }
            // Refresh all data
            queryClient.invalidateQueries({ queryKey: ['sales-summary'] });
            queryClient.invalidateQueries({ queryKey: ['chart-data'] });
            queryClient.invalidateQueries({ queryKey: ['recent-sales'] });
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            break;

          case 'sync-completed':
          case 'sync_complete':
          case 'data-refreshed':
            // Invalidate ALL queries to refresh data after sync
            console.log('[WebSocket] Data refresh event received, invalidating all queries');
            queryClient.invalidateQueries({ queryKey: ['sales-summary'] });
            queryClient.invalidateQueries({ queryKey: ['chart-data'] });
            queryClient.invalidateQueries({ queryKey: ['recent-sales'] });
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            queryClient.invalidateQueries({ queryKey: ['sync-status'] });
            break;

          case 'new_transaction':
            // Invalidate relevant queries
            queryClient.invalidateQueries({ queryKey: ['sales-summary'] });
            queryClient.invalidateQueries({ queryKey: ['chart-data'] });
            queryClient.invalidateQueries({ queryKey: ['recent-sales'] });
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            break;
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason);
      wsRef.current = null;

      // Reconnect after delay if not intentionally closed
      if (isAuthenticated && event.code !== 1000) {
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 5000);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    wsRef.current = ws;
  }, [isAuthenticated, trigger, triggerChampion, queryClient]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User logout');
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [isAuthenticated, connect, disconnect]);

  return { connect, disconnect };
}
